import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Modal } from "../UI/Modal";
import { FormField } from "../UI/FormField";
import Button from "../UI/Button";
import type { GenerateLinesPayload } from "../../types/containerOrders";

interface GenerateLinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (payload: GenerateLinesPayload) => void;
  generating: boolean;
  orderNumber: string;
  /** Containers already on the order — drives the "#04–#09" preview. */
  existingCount: number;
}

const MIN = 1;
const MAX = 100;

/**
 * "Add N containers" — bulk creation of lines from the order's defaults.
 *
 * The count is the only input. A per-run collection date used to sit beside it;
 * it was removed once the order's three stops grew their own pickup / on-site /
 * delivery slots, which say the same thing in the place the whole batch already
 * inherits from — and said it twice, from two screens, whenever both were set.
 * A container that really does move on another day is corrected on the
 * container's own page afterwards.
 */
export function GenerateLinesModal({
  isOpen,
  onClose,
  onGenerate,
  generating,
  orderNumber,
  existingCount,
}: GenerateLinesModalProps) {
  const { t } = useTranslation("containerOrders");

  const [count, setCount] = useState(1);

  const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n));

  // Positions are never reused, so the next one always follows the highest
  // already handed out — which is what `existingCount` reflects.
  const from = existingCount + 1;
  const to = existingCount + count;

  const submit = () => {
    if (generating) return;
    onGenerate({ count });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("generate.title", { orderNumber })}
      size="md"
    >
      {/* Modal renders children flush against its shell, so the body owns its
          own padding and scroll area and the footer its own bar. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 space-y-5">
          <p className="text-[13px] leading-relaxed text-[#8A9590]">
            {t("generate.description")}
          </p>

          <FormField label={t("generate.countLabel")} layout="inline" required>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCount((n) => clamp(n - 1))}
                disabled={count <= MIN || generating}
                aria-label={t("generate.decrease")}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[rgba(11,15,14,0.15)] bg-white text-[#0B0F0E] hover:bg-[#F5F4F0] disabled:opacity-40 disabled:hover:bg-white"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={MIN}
                max={MAX}
                value={count}
                onChange={(e) => setCount(clamp(Number(e.target.value) || MIN))}
                disabled={generating}
                // Native spinners would sit right beside the -/+ buttons.
                className="h-9 w-16 rounded-md border border-[rgba(11,15,14,0.15)] bg-white px-2 text-center text-[13px] text-[#0B0F0E] focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={() => setCount((n) => clamp(n + 1))}
                disabled={count >= MAX || generating}
                aria-label={t("generate.increase")}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[rgba(11,15,14,0.15)] bg-white text-[#0B0F0E] hover:bg-[#F5F4F0] disabled:opacity-40 disabled:hover:bg-white"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </FormField>

          {existingCount > 0 && (
            <p className="rounded-lg bg-[#F5F4F0] px-3 py-2 text-[12px] leading-relaxed text-[#0B0F0E]">
              {t("generate.appendNotice", {
                existing: existingCount,
                from: String(from).padStart(2, "0"),
                to: String(to).padStart(2, "0"),
              })}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[rgba(11,15,14,0.08)] bg-white px-4 py-3 sm:px-6 shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={generating}
          >
            {t("generate.cancel")}
          </Button>
          <Button type="submit" variant="primary" isLoading={generating}>
            {t("generate.confirm", { count })}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
